import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import {
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
} from '@mui/material'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { GroupSectionAnalysisState } from '../../../../features/types'
import ProgressBar from '../../../../components/progressBar/ProgressBar'
import { tableStyles, ui } from '../../../../utils/uiTokens'
import { capitalize } from '../../../../utils/functions'
import { DEFAULT_PAGE_SIZE } from '../../../../utils/constants'

import { useGroupLabel } from '../../../head/headScope'

interface Props {
    groups: GroupSectionAnalysisState[]
    isFetching: boolean
}

const GroupsTable: React.FC<Props> = ({ groups = [], isFetching = false }) => {
    const { formatMessage } = useIntl()
    const groupLabel = useGroupLabel()
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE)
    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - groups.length) : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={tableStyles.container}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={tableStyles.head}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell>
                                {formatMessage({ id: 'group' })}
                            </TableCell>

                            <TableCell>
                                {capitalize(
                                    formatMessage({ id: 'videoLesson' }),
                                )}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'test' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'actions' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {(rowsPerPage > 0
                            ? groups.slice(
                                  page * rowsPerPage,
                                  page * rowsPerPage + rowsPerPage,
                              )
                            : groups
                        ).map((row, index) => (
                            <TableRow key={row.groupId} sx={tableStyles.row}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    width={4}
                                >
                                    {index + 1}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {groupLabel({ id: row.groupId, title: row.title })}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    <ProgressBar value={row.videoPercentage} />
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    <ProgressBar value={row.testPercentage} />
                                </TableCell>

                                <TableCell
                                    style={{ width: 60 }}
                                    align="right"
                                >
                                    <Link
                                        to={`${row.groupId}/students`}
                                        state={{ group: row }}
                                    >
                                        <IconButton
                                            sx={{ background: ui.pinkSoft, borderRadius: '10px' }}
                                        >
                                            <img
                                                src={ArrowRightIcon}
                                                alt="edit"
                                            />
                                        </IconButton>
                                    </Link>
                                </TableCell>
                            </TableRow>
                        ))}

                        {!isFetching && groups.length === 0 && (
                            <TableRow>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    align="center"
                                    colSpan={5}
                                >
                                    {formatMessage({ id: 'noData' })}
                                </TableCell>
                            </TableRow>
                        )}

                        {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={5} />
                            </TableRow>
                        )}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={5}
                                count={groups.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default GroupsTable
